import classNames from 'classnames';
import React from 'react';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import { PostType } from '../graphql/generated/graphql';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import styles from './PostDetailPreviewStateBar.module.scss';
import { getStateIcon } from './PostIcon';
import VerifiedIcon from './VerifiedIcon';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';

const ICON_SIZE = 14;

const POST_FRAGMENT = gql(`
  fragment PostDetailPreviewStateBar_post on Post {
    id
    archivedAt
    publishedAt
    type
    verifiedAt
    ...getVerificationStatus_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

const isTemplate = (post: PostFragment) => post.type === PostType.Template;
const isDraft = (post: PostFragment) => !post.publishedAt;
const isArchived = (post: PostFragment) => !!post.archivedAt;
const isVerified = (post: PostFragment) =>
  getVerificationStatus(post) === VerificationStatus.Verified;

const getClassName = (post: PostFragment) => {
  if (isArchived(post)) {
    return styles.archived;
  }
  if (isDraft(post)) {
    return styles.draft;
  }
  if (isVerified(post)) {
    return styles.verified;
  }
  if (isTemplate(post)) {
    return styles.template;
  }
  return null;
};

const getIcon = (post: PostFragment) => {
  if (isVerified(post)) {
    return <VerifiedIcon size={ICON_SIZE} />;
  }

  return (
    <SVGIcon size={ICON_SIZE}>
      {getStateIcon({
        type: post.type,
        draft: isDraft(post),
        archived: isArchived(post),
      })}
    </SVGIcon>
  );
};

const getTitle = (post: PostFragment) => {
  if (isDraft(post)) {
    if (isTemplate(post)) {
      return 'Draft Template';
    }
    return 'Draft';
  }
  if (isTemplate(post)) {
    return isVerified(post) ? 'Template Verified' : 'Template';
  }
  if (isArchived(post)) {
    return 'Archived';
  }
  if (isVerified(post)) {
    return 'Verified';
  }
  return null;
};

const getDescription = (post: PostFragment) => {
  if (isArchived(post)) {
    return (
      <>
        {isDraft(post) || isTemplate(post) ? ' archived ' : ' '}
        <Timestamp date={post.archivedAt} />
      </>
    );
  }
  if (isVerified(post)) {
    return ` on ${yearSensitiveDateString(post.verifiedAt)}`;
  }
  return null;
};

export const hasStateBar = (post?: PostFragment) =>
  post &&
  (isTemplate(post) || isDraft(post) || isArchived(post) || isVerified(post));

interface PostDetailPreviewStateBarProps {
  post: PostFragment;
  contentMaxWidth?: number;
  className?: string;
}

const PostDetailPreviewStateBar = ({
  post,
  contentMaxWidth,
  className,
}: PostDetailPreviewStateBarProps) => {
  if (!hasStateBar(post)) {
    return null;
  }

  return (
    <div
      className={classNames(styles.container, getClassName(post), className)}
    >
      <div className={styles.content} style={{ maxWidth: contentMaxWidth }}>
        {getIcon(post)}
        <span>
          <strong>{getTitle(post)}</strong>
          {getDescription(post)}
        </span>
      </div>
    </div>
  );
};

export default PostDetailPreviewStateBar;
